import type { ApiCreateOrderRequest } from '../../api/order/requests/orderDirectApiRequests';

import { appInsights } from '../../../../appInsights';
import { Cookies } from '../../../../utility/cookies';
import { getAuthToken } from '../../api/auth/getAuthToken';
import { orderDirectApi } from '../../api/order/orderDirectApi';
import { COOP_GA_ORDER_COUNT_SENT } from '../../tracking/ga4/consts';
import { currentCartFlow } from '../cart/currentCartFlow';
import mapOrder, { mapOrderHistory } from './mapOrder';

interface CreateOrderRequest {
    // subscription frequency (1-4)
    subscriptionFrequency?: number | null;
    // payment transaction request ID, for intrum this is the customers reference (default the one on billing address, but user can change)
    transaction: string;
}

const orderFlow = {
    createOrder: async (request?: CreateOrderRequest) => {
        const { token, shoppingUserId } = await getAuthToken();
        const cartId = currentCartFlow.getCurrentCartId();

        if (!cartId) {
            const error = Error('No cart id');
            appInsights.trackException({ exception: error }, { place: 'createOrder' });
            throw error;
        }

        const apiRequest: ApiCreateOrderRequest = {
            userId: shoppingUserId,
            cartId,
            isSubscription: !!request?.subscriptionFrequency,
            transaction: request?.transaction ?? undefined,
            frequency: request?.subscriptionFrequency ?? undefined,
        };
        const order = await orderDirectApi.createOrder(token)(apiRequest);

        Cookies.eraseCookie(COOP_GA_ORDER_COUNT_SENT); // we want to make sure this cookie is removed when creating an order, to allow sending order count to GA on order complete page

        return order;
    },
    getOrderHistory: async (currentPage: number, pageSize: number) => {
        const { token, shoppingUserId } = await getAuthToken();
        const resp = await orderDirectApi.getOrderHistory(token)({
            userId: shoppingUserId,
            currentPage,
            pageSize,
        });

        const mapped = resp.map(mapOrderHistory).filter(Boolean);

        return mapped;
    },
    getOrderById: async (orderId: string, fields?: 'FULL') => {
        const { token, shoppingUserId } = await getAuthToken();
        const resp = await orderDirectApi.getOrderById(token)({
            userId: shoppingUserId,
            orderId,
            fields,
        });
        const mapped = mapOrder(resp);
        return mapped;
    },
    cancelOrder: async (orderId: string) => {
        const { token, shoppingUserId } = await getAuthToken();
        const resp = await orderDirectApi.cancelOrder(token)({
            userId: shoppingUserId,
            orderId,
        });
        return resp;
    },
    getOrderCount: async () => {
        const { token, shoppingUserId } = await getAuthToken();
        const resp = await orderDirectApi.getOrderCount(token)({ userId: shoppingUserId });
        return resp;
    },
    // EDIT ORDER
    editOrder: async (orderId: string) => {
        const { token, shoppingUserId } = await getAuthToken();
        const resp = await orderDirectApi.editOrder(token)({
            userId: shoppingUserId,
            orderId,
        });
        return resp;
    },
    cancelEditOrder: async (orderId: string) => {
        const { token, shoppingUserId } = await getAuthToken();
        const resp = await orderDirectApi.cancelEditOrder(token)({
            userId: shoppingUserId,
            orderId,
        });
        return resp;
    },
    completeEditOrder: async (cartId: string) => {
        const { token, shoppingUserId } = await getAuthToken();
        const resp = await orderDirectApi.completeEditOrder(token)({
            userId: shoppingUserId,
            cartId,
        });
        return resp;
    },
};

export default orderFlow;
